// M004-F0646..F0648 (design M004-F0646-F0649; Core M005-D004 §10.12-§10.15,
// Core MR !938, M005-F0081..F0084) - the price-plan calls, in their OWN module
// (not appended to services/api.js) so they stay on a disjoint path from the
// other lanes that are adding to that shared client; it reuses the one axios
// instance (auth header, interceptors, step-up 401 handling) via the default
// export of ./api.
//
// Built against the CONTRACT, not against a deployed Core: every caller is
// behind REACT_APP_MEMBER_PRICE_PLANS_ENABLED / REACT_APP_BOOKING_PRICE_SECTION_ENABLED
// (src/utils/priceFeatureFlags.js). snake_case throughout. The client does NO
// rate arithmetic - amounts, impact and coverage are Core's.
//
// Existing calls this feature also uses stay where they are, in pricingApi:
// getMemberPlanAssignments (GET assignments?user_id, now richer - A7),
// getPlans (GET plans, rows gain zones[] - W4) and setBookingPrice
// (POST bookings/:id/price, now {mode, scope, reason} - P3).
import api from './api';

const dryRunConfig = (options) => (options?.dryRun === true ? { params: { dry_run: 'true' } } : undefined);

const org = (organisationId) => `/v1/orgs/${encodeURIComponent(organisationId)}/pricing`;

export const pricePlanApi = {
  // A1: atomic batch add. Body {user_id, items:[{pricing_plan_id,
  // effective_from, effective_to?}], grandfather?}.
  batchAssignMemberPlans: (organisationId, body) => api.post(`${org(organisationId)}/assignments/batch`, body),
  // A2/A3: change dates / end-date. Body {effective_from?, effective_to?, grandfather?}.
  patchAssignment: (organisationId, assignmentId, body) => api.patch(
    `${org(organisationId)}/assignments/${encodeURIComponent(assignmentId)}`,
    body,
  ),
  // A3: permitted only for a never-effective (future) row.
  deleteAssignment: (organisationId, assignmentId) => api.delete(
    `${org(organisationId)}/assignments/${encodeURIComponent(assignmentId)}`,
  ),
  // A4: dry-run impact. Body {user_id, add:[{pricing_plan_id, effective_from}],
  // end:[{assignment_id, effective_to}]}.
  assignmentImpact: (organisationId, body) => api.post(`${org(organisationId)}/assignments/impact`, body),
  // M005-F0082: server-evaluated weekly coverage for one member + room.
  getMemberCoverage: (organisationId, userId, params) => api.get(
    `${org(organisationId)}/members/${encodeURIComponent(userId)}/coverage`,
    { params },
  ),
  // M005-F0082: same shape for a hypothetical candidate set (A4 body + room_id).
  previewMemberCoverage: (organisationId, userId, body) => api.post(
    `${org(organisationId)}/members/${encodeURIComponent(userId)}/coverage/preview`,
    body,
  ),
  // M005-D005 §7.6.1 + M005-D004 §10.15 P1 (`current.basis`).
  // `occurrenceDate` (YYYY-MM-DD) names ONE date of a series (M005-D004 §10.16 item 3);
  // Core answers 409 PRICING_OCCURRENCE_HAS_NO_ROW for a date that has no row of its own.
  getBookingPricing: (organisationId, bookingId, occurrenceDate) => api.get(
    `${org(organisationId)}/bookings/${encodeURIComponent(bookingId)}/pricing`,
    occurrenceDate ? { params: { occurrence_date: occurrenceDate } } : undefined,
  ),
  // M005-D004 §10.15 P6: dry run of a P3/P5 body.
  bookingPriceImpact: (organisationId, bookingId, body) => api.post(
    `${org(organisationId)}/bookings/${encodeURIComponent(bookingId)}/price/impact`,
    body,
  ),
  // ---- M005-F0085 zone authoring (M005-D004 §10.14 W1-W4) ----
  // W1: org-scoped windows. A zone is a window linked to a plan with
  // applies_only_within. Window body: {name, weekday_mask (1..127), start_local,
  // end_local (start < end, no midnight span), booking_shape, min_booking_minutes?,
  // access_mode, release_lead_months?}.
  listWindows: (organisationId) => api.get(`${org(organisationId)}/windows`),
  createWindow: (organisationId, body) => api.post(`${org(organisationId)}/windows`, body),
  // Zone writes take `{dryRun: true}` -> `?dry_run=true`: Core evaluates the hypothetical zone set
  // and answers `{..., propagation:{to_reprice, held, refused_billed, ..., dry_run:true}}` writing nothing.
  patchWindow: (organisationId, windowId, body, options) => api.patch(
    `${org(organisationId)}/windows/${encodeURIComponent(windowId)}`,
    body,
    dryRunConfig(options),
  ),
  // W2: create/update the plan<->window link {price_mode, flat_price_minor?, applies_only_within}.
  putPlanWindowLink: (organisationId, planId, windowId, body, options) => api.put(
    `${org(organisationId)}/plans/${encodeURIComponent(planId)}/windows/${encodeURIComponent(windowId)}`,
    body,
    dryRunConfig(options),
  ),
  deletePlanWindowLink: (organisationId, planId, windowId, options) => api.delete(
    `${org(organisationId)}/plans/${encodeURIComponent(planId)}/windows/${encodeURIComponent(windowId)}`,
    dryRunConfig(options),
  ),
};

export default pricePlanApi;
